import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Paper,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Chip,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import BoxedTabs from './components/BoxedTabs';
import CircleRail from './components/CircleRail';
import CircleDetailPanel from './components/CircleDetailPanel';
import IntersectionView from './components/IntersectionView';
import ConnectionRequests from './components/ConnectionRequests';
import ConnectionsView from './components/ConnectionsView';
import { networkDataService } from './services/networkData';
import networkApi from './services/networkApi';
import AddToCirclePrompt from '../../components/network/AddToCirclePrompt';


const Network = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const notify = useRef(showNotification);
  useEffect(() => { notify.current = showNotification; }, [showNotification]);

  // ── Core data state ───────────────────────────────────────────────────────
  const [loading, setLoading]                       = useState(true);
  const [circles, setCircles]                       = useState([]);
  const [connections, setConnections]               = useState([]);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [sentConnectionRequests, setSentConnectionRequests] = useState([]);
  const [connectedUserIds, setConnectedUserIds]     = useState(new Set());
  const [sentPendingIds, setSentPendingIds]         = useState(new Set());
  const [addToCirclePrompt, setAddToCirclePrompt]   = useState(null); // { requestId, personName } | null

  // ── UI / view state ───────────────────────────────────────────────────────
  const [mainTab, setMainTab] = useState('connections'); // 'circles' | 'connections'
  const [connectionsTab, setConnectionsTab] = useState(0);

  // ── Circle rail / detail state ────────────────────────────────────────────
  const [circlesViewMode, setCirclesViewMode] = useState('circle'); // 'circle' | 'intersections'
  const [selectedCircleId, setSelectedCircleId] = useState(null);
  const [intersectionSelected, setIntersectionSelected] = useState(null);
  const [intersectionHovered, setIntersectionHovered] = useState(null);

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchQuery,    setSearchQuery]    = useState('');
  const [searchResults,  setSearchResults]  = useState([]);
  const [searchLoading,  setSearchLoading]  = useState(false);
  const [searchedOnce,   setSearchedOnce]   = useState(false); // true after first submit
  const [sendingId,      setSendingId]      = useState(null); // customer_id being requested
  const [revokingId,     setRevokingId]     = useState(null); // request_id being revoked

  // ── Create circle dialog ──────────────────────────────────────────────────
  const [showCreateCircle, setShowCreateCircle] = useState(false);
  const [newCircleName,    setNewCircleName]    = useState('');
  const [newCircleColor,   setNewCircleColor]   = useState('#4ECDC4');

  // ── Edit circle dialog ────────────────────────────────────────────────────
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCircle,     setEditCircle]     = useState(null);
  const [editedName,     setEditedName]     = useState('');

  // ── Single add-member dialog (search + relationship in one dialog) ─────────
  const [addMemberOpen,    setAddMemberOpen]    = useState(false);
  const [addMemberCircle,  setAddMemberCircle]  = useState(null);   // target circle
  const [addMemberSearch,  setAddMemberSearch]  = useState('');
  const [addMemberResults, setAddMemberResults] = useState([]);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const [addMemberSearchedOnce, setAddMemberSearchedOnce] = useState(false);
  const [addMemberPerson,  setAddMemberPerson]  = useState(null);   // {id, name, username}
  const [addMemberBusy,    setAddMemberBusy]    = useState(false);
  const [addMemberShareHistory, setAddMemberShareHistory] = useState(false);
  const [addMemberHistoryDays,  setAddMemberHistoryDays]  = useState('');

  // ── Manage members dialog ─────────────────────────────────────────────────
  const [manageMembersOpen,    setManageMembersOpen]    = useState(false);
  const [manageMembersCircle,  setManageMembersCircle]  = useState(null);
  const [manageMembersData,    setManageMembersData]    = useState([]);
  const [manageMembersLoading, setManageMembersLoading] = useState(false);
  const [removingMemberId,     setRemovingMemberId]     = useState(null);

  // ── Load all data ─────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [graphData, requestsData, connectionsData] = await Promise.all([
        networkApi.getNetworkGraph(),
        networkApi.getConnectionRequests(),
        networkApi.getConnections(),
      ]);
      networkDataService.loadFromAPI(graphData);
      setCircles(networkDataService.getCircles());
      setConnections(Array.isArray(connectionsData) ? connectionsData : []);
      setConnectionRequests(requestsData.received || []);
      setSentConnectionRequests(requestsData.sent || []);
      setConnectedUserIds(new Set((connectionsData || []).map(c => c.customer_id)));
      setSentPendingIds(new Set((requestsData.sent || []).map(r => r.receiver_id)));
    } catch {
      notify.current('Failed to load network data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Keep the selected circle valid as circles load/change.
  useEffect(() => {
    if (circles.length === 0) {
      setSelectedCircleId(null);
      return;
    }
    if (!circles.some(c => c.id === selectedCircleId)) {
      setSelectedCircleId(circles[0].id);
    }
  }, [circles, selectedCircleId]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const refreshCircles = () => setCircles([...networkDataService.getCircles()]);

  // ── Global search ─────────────────────────────────────────────────────────

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) { setSearchResults([]); setSearchedOnce(false); return; }
    setSearchLoading(true);
    setSearchedOnce(true);
    try {
      const results = await networkApi.searchUsers(searchQuery.trim());
      setSearchResults(results);
    } catch {
      showNotification('Search failed', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSendRequest = async (user) => {
    setSendingId(user.customer_id);
    try {
      const sent = await networkApi.sendConnectionRequest({ receiver_id: user.customer_id });
      setSentConnectionRequests(prev => [
        {
          id: sent.request_id,
          receiver_id: user.customer_id,
          receiver_name: user.customer_name,
          username: user.username,
          status: 'pending',
        },
        ...prev,
      ]);
      setSentPendingIds(prev => new Set([...prev, user.customer_id]));
      showNotification(`Connection request sent to ${user.customer_name}`, 'success');
      setAddToCirclePrompt({ requestId: sent.request_id, personName: user.customer_name });
    } catch (err) {
      showNotification(err.message || 'Failed to send request', 'error');
    } finally {
      setSendingId(null);
    }
  };

  const handleRevokeRequest = async (requestId) => {
    setRevokingId(requestId);
    try {
      const revoked = sentConnectionRequests.find(r => r.id === requestId);
      await networkApi.revokeConnectionRequest(requestId);
      setSentConnectionRequests(prev => prev.filter(r => r.id !== requestId));
      if (revoked?.receiver_id) {
        setSentPendingIds(prev => {
          const next = new Set(prev);
          next.delete(revoked.receiver_id);
          return next;
        });
      }
      showNotification(
        `Connection request ${revoked?.receiver_name ? `to ${revoked.receiver_name} ` : ''}revoked`,
        'info'
      );
    } catch (err) {
      showNotification(err.message || 'Failed to revoke request', 'error');
    } finally {
      setRevokingId(null);
    }
  };

  // ── Create circle ─────────────────────────────────────────────────────────

  const handleCreateCircle = async () => {
    if (!newCircleName.trim()) return;
    try {
      const created = await networkApi.createCircle({ circle_name: newCircleName.trim(), color: newCircleColor });
      networkDataService.syncCircleAdd(created);
      refreshCircles();
      setShowCreateCircle(false);
      setNewCircleName('');
      setNewCircleColor('#4ECDC4');
      showNotification('Circle created!', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to create circle', 'error');
    }
  };

  // ── Update / Delete circle ────────────────────────────────────────────────

  const handleUpdateCircle = async (circleId, updates) => {
    try {
      await networkApi.updateCircle(circleId, updates);
      networkDataService.syncCircleUpdate(circleId, { ...updates, ...(updates.circle_name ? { name: updates.circle_name } : {}) });
      refreshCircles();
      showNotification('Circle updated!', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to update circle', 'error');
    }
  };

  const handleDeleteCircle = async (circleId) => {
    try {
      await networkApi.deleteCircle(circleId);
      networkDataService.syncCircleRemove(circleId);
      refreshCircles();
      showNotification('Circle deleted', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to delete circle', 'error');
    }
  };

  // ── Edit circle dialog ────────────────────────────────────────────────────

  const handleEditCircle = (circle) => {
    setEditCircle(circle);
    setEditedName(circle.name);
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editCircle || !editedName.trim()) return;
    await handleUpdateCircle(editCircle.id, { circle_name: editedName.trim() });
    setEditDialogOpen(false);
    setEditCircle(null);
    setEditedName('');
  };

  // ── Add member flow (single dialog) ──────────────────────────────────────

  const openAddMember = (circle) => {
    setAddMemberCircle(circle);
    setAddMemberSearch('');
    setAddMemberResults([]);
    setAddMemberPerson(null);
    setAddMemberSearchedOnce(false);
    setAddMemberOpen(true);
  };

  const handleAddMemberSearch = async (e) => {
    e.preventDefault();
    if (!addMemberSearch.trim()) return;
    setAddMemberLoading(true);
    setAddMemberSearchedOnce(true);
    try {
      const results = await networkApi.searchConnections(addMemberSearch.trim());
      setAddMemberResults(results);
    } catch {
      showNotification('Search failed', 'error');
    } finally {
      setAddMemberLoading(false);
    }
  };

  const handleConfirmAddMember = async () => {
    if (!addMemberPerson || !addMemberCircle) return;
    setAddMemberBusy(true);
    const historyDaysParsed = addMemberHistoryDays ? parseInt(addMemberHistoryDays, 10) : null;
    try {
      await networkApi.addMemberToCircle(addMemberCircle.id, {
        customer_id: addMemberPerson.customer_id,
        zone_level: 1,
        share_history: addMemberShareHistory ? 1 : 0,
        history_days: addMemberShareHistory ? historyDaysParsed : null,
      });
      networkDataService.syncMemberAdd(addMemberCircle.id, {
        customer_id: addMemberPerson.customer_id,
        customer_name: addMemberPerson.customer_name,
        zone_level: 1,
        username: addMemberPerson.username,
      });
      refreshCircles();
      setAddMemberOpen(false);
      showNotification('Member added!', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to add member', 'error');
    } finally {
      setAddMemberBusy(false);
    }
  };

  const handleAddExistingMemberToCircle = async (member, circle) => {
    if (!member || !circle) return;
    const relationship = member.role || 'member';
    const zoneLevel = member.level || 1;

    try {
      await networkApi.addMemberToCircle(circle.id, {
        customer_id: member.id,
        relationship,
        zone_level: zoneLevel,
      });
      networkDataService.syncMemberAdd(circle.id, {
        customer_id: member.id,
        customer_name: member.name,
        relationship,
        zone_level: zoneLevel,
        username: member.username,
        profile_picture: member.profile_picture,
      });
      refreshCircles();
      showNotification(`${member.name} added to ${circle.name}`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to add member to circle', 'error');
      throw err;
    }
  };

  const handleRemoveMemberFromCircle = async (member, circle) => {
    if (!member || !circle) return;
    try {
      await networkApi.removeMemberFromCircle(circle.id, member.id);
      networkDataService.syncMemberRemove(circle.id, member.id);
      refreshCircles();
      showNotification(`${member.name} removed from ${circle.name}`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to remove member from circle', 'error');
      throw err;
    }
  };

  const handleDisconnectMember = async (member) => {
    if (!member) return;
    try {
      await networkApi.disconnectConnection(member.id);
      circles.forEach(circle => {
        if ((circle.membersData || []).some(m => m.id === member.id)) {
          networkDataService.syncMemberRemove(circle.id, member.id);
        }
      });
      setConnections(prev => prev.filter(c => c.customer_id !== member.id));
      setConnectedUserIds(prev => {
        const next = new Set(prev);
        next.delete(member.id);
        return next;
      });
      refreshCircles();
      showNotification(`${member.name} disconnected`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to disconnect member', 'error');
      throw err;
    }
  };

  // Called from ConnectionsView with all data resolved - calls API directly.
  const handleAddConnectionToCircle = async (connection, circle, shareHistory, historyDays) => {
    try {
      await networkApi.addMemberToCircle(circle.id, {
        customer_id: connection.customer_id,
        zone_level: 1,
        share_history: shareHistory ? 1 : 0,
        history_days: shareHistory ? historyDays : null,
      });
      networkDataService.syncMemberAdd(circle.id, {
        customer_id: connection.customer_id,
        customer_name: connection.customer_name,
        zone_level: 1,
        username: connection.username,
        profile_picture: connection.profile_picture,
      });
      refreshCircles();
      showNotification(`${connection.customer_name} added to ${circle.name}`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to add member', 'error');
      throw err;
    }
  };

  const closeAddMember = () => {
    setAddMemberOpen(false);
    setAddMemberCircle(null);
    setAddMemberPerson(null);
    setAddMemberSearch('');
    setAddMemberResults([]);
    setAddMemberShareHistory(false);
    setAddMemberHistoryDays('');
    setAddMemberSearchedOnce(false);
  };

  // ── Manage members dialog ─────────────────────────────────────────────────

  const openManageMembers = async (circle) => {
    setManageMembersCircle(circle);
    setManageMembersData([]);
    setManageMembersOpen(true);
    setManageMembersLoading(true);
    try {
      const members = await networkApi.getCircleMembers(circle.id);
      setManageMembersData(Array.isArray(members) ? members : []);
    } catch {
      showNotification('Failed to load members', 'error');
    } finally {
      setManageMembersLoading(false);
    }
  };

  const handleRemoveMemberFromManage = async (member) => {
    setRemovingMemberId(member.customer_id);
    try {
      await networkApi.removeMemberFromCircle(manageMembersCircle.id, member.customer_id);
      networkDataService.syncMemberRemove(manageMembersCircle.id, member.customer_id);
      setManageMembersData(prev => prev.filter(m => m.customer_id !== member.customer_id));
      refreshCircles();
      showNotification(`${member.customer_name} removed from ${manageMembersCircle.name}`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to remove member', 'error');
    } finally {
      setRemovingMemberId(null);
    }
  };

  // ── Connection requests ───────────────────────────────────────────────────

  const handleAcceptRequest = async (requestId, circleId) => {
    try {
      await networkApi.respondToRequest(requestId, { action: 'accept', circle_id: circleId || undefined });
      setConnectionRequests(prev => prev.filter(r => r.id !== requestId));
      if (circleId) await loadData();
      showNotification('Connection request accepted!', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to accept request', 'error');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await networkApi.respondToRequest(requestId, { action: 'reject' });
      setConnectionRequests(prev => prev.filter(r => r.id !== requestId));
      showNotification('Connection request rejected', 'info');
    } catch (err) {
      showNotification(err.message || 'Failed to reject', 'error');
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const getConnectionLabel = (user) => {
    const id = user.customer_id;
    if (connectedUserIds.has(id)) return 'connected';
    if (sentPendingIds.has(id)) return 'pending';
    return 'none';
  };

  const selectedCircle = circles.find(c => c.id === selectedCircleId) || null;

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: isMobile ? 8 : 0 }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* ── Page Header ────────────────────────────────────────────────── */}
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>Your Network</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Search for people, manage circles, and handle connection requests.
        </Typography>

        {/* ── Search bar ─────────────────────────────────────────────────── */}
        <Box component="form" onSubmit={handleSearch} sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Search by name or username..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
          />
          <Button type="submit" variant="contained" sx={{ px: 3, borderRadius: 2, textTransform: 'none' }} disabled={searchLoading}>
            {searchLoading ? <CircularProgress size={20} /> : 'Search'}
          </Button>
        </Box>

        {/* ── Search results (shown immediately below search, above tabs) ─ */}
        {searchedOnce && (
          <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {searchLoading ? 'Searching…' : searchResults.length === 0 ? 'No results' : `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`}
              </Typography>
            </Box>
            {!searchLoading && searchResults.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No users found for "{searchQuery}". Try a different name or username.
                </Typography>
              </Box>
            )}
            <List disablePadding>
              {searchResults.map((user, idx) => {
                const connStatus = getConnectionLabel(user);
                const sentRequest = sentConnectionRequests.find(r => r.receiver_id === user.customer_id);
                const canViewProfile = user.is_public && user.username;
                return (
                  <React.Fragment key={user.customer_id}>
                    {idx > 0 && <Divider />}
                    <ListItem sx={{ py: 1.5, px: 2 }}>
                      <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: theme.palette.primary.light }}>
                        {(user.customer_name || '?')[0].toUpperCase()}
                      </Avatar>
                      <ListItemText
                        sx={{ minWidth: 0 }}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {user.customer_name}
                            </Typography>
                            {user.username && (
                              <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
                            )}
                            {connStatus === 'connected' && (
                              <Chip label="Connected" size="small" color="success" variant="outlined" />
                            )}
                            {connStatus === 'pending' && (
                              <Chip label="Request sent" size="small" color="default" variant="outlined" />
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {canViewProfile && (
                          <Tooltip title="View profile">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/profile/${user.username}`)}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {connStatus === 'none' && (
                          <Tooltip title="Send connection request">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSendRequest(user)}
                                disabled={sendingId === user.customer_id}
                              >
                                {sendingId === user.customer_id
                                  ? <CircularProgress size={18} />
                                  : <PersonAddIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {connStatus === 'pending' && sentRequest && (
                          <Tooltip title="Revoke connection request">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRevokeRequest(sentRequest.id)}
                                disabled={revokingId === sentRequest.id}
                              >
                                {revokingId === sentRequest.id
                                  ? <CircularProgress size={18} />
                                  : <CloseIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}

        {/* ── Section switcher ──────────────────────────────────────────── */}
        <BoxedTabs
          value={mainTab}
          onChange={setMainTab}
          items={[
            { value: 'connections', label: 'Your Connections', badge: connectionRequests.length + sentConnectionRequests.length },
            { value: 'circles', label: 'Your Circles' },
          ]}
        />

        {/* ── Circles ────────────────────────────────────────────────────── */}
        {mainTab === 'circles' && (
        <Box sx={{ mb: 5 }}>
          <CircleRail
            circles={circles}
            selectedId={selectedCircleId}
            viewMode={circlesViewMode}
            onSelectCircle={(id) => { setCirclesViewMode('circle'); setSelectedCircleId(id); }}
            onShowIntersections={() => setCirclesViewMode('intersections')}
            onCreateCircle={() => setShowCreateCircle(true)}
          />

          {circlesViewMode === 'intersections' ? (
            <IntersectionView
              circles={circles}
              onCircleClick={(circle) => setIntersectionSelected(prev => prev === circle.id ? null : circle.id)}
              onEditClick={(e, circle) => { e.stopPropagation(); handleEditCircle(circle); }}
              onAddMemberClick={openAddMember}
              selectedCircle={intersectionSelected}
              hoveredCircle={intersectionHovered}
              setHoveredCircle={setIntersectionHovered}
            />
          ) : selectedCircle ? (
            <CircleDetailPanel
              circle={selectedCircle}
              onEditCircle={handleEditCircle}
              onDeleteCircle={handleDeleteCircle}
              onAddMember={openAddMember}
              onManageMembers={openManageMembers}
            />
          ) : (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                No circles yet. Create your first circle to start organizing your connections.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateCircle(true)} sx={{ textTransform: 'none', borderRadius: 2 }}>
                Create Circle
              </Button>
            </Paper>
          )}
        </Box>
        )}

        {/* ── Connections ────────────────────────────────────────────────── */}
        {mainTab === 'connections' && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              label="Connections"
              icon={<PeopleIcon />}
              clickable
              color={connectionsTab === 0 ? 'primary' : 'default'}
              variant={connectionsTab === 0 ? 'filled' : 'outlined'}
              onClick={() => setConnectionsTab(0)}
            />
            <Chip
              label={`Pending Requests${connectionRequests.length ? ` (${connectionRequests.length})` : ''}`}
              icon={<MarkEmailUnreadIcon />}
              clickable
              color={connectionsTab === 1 ? 'primary' : 'default'}
              variant={connectionsTab === 1 ? 'filled' : 'outlined'}
              onClick={() => setConnectionsTab(1)}
            />
            <Chip
              label={`Sent Requests${sentConnectionRequests.length ? ` (${sentConnectionRequests.length})` : ''}`}
              icon={<SendIcon />}
              clickable
              color={connectionsTab === 2 ? 'primary' : 'default'}
              variant={connectionsTab === 2 ? 'filled' : 'outlined'}
              onClick={() => setConnectionsTab(2)}
            />
          </Box>

          {connectionsTab === 0 && (
            <ConnectionsView
              connections={connections}
              circles={circles}
              onAddToCircle={handleAddConnectionToCircle}
              onRemoveMemberFromCircle={handleRemoveMemberFromCircle}
              onDisconnectMember={handleDisconnectMember}
            />
          )}
          {connectionsTab === 1 && (
            <ConnectionRequests
              show="received"
              requests={connectionRequests}
              sentRequests={sentConnectionRequests}
              circles={circles}
              onAccept={handleAcceptRequest}
              onReject={handleRejectRequest}
              onRevoke={handleRevokeRequest}
              revokingId={revokingId}
            />
          )}
          {connectionsTab === 2 && (
            <ConnectionRequests
              show="sent"
              requests={connectionRequests}
              sentRequests={sentConnectionRequests}
              circles={circles}
              onAccept={handleAcceptRequest}
              onReject={handleRejectRequest}
              onRevoke={handleRevokeRequest}
              revokingId={revokingId}
            />
          )}
        </Box>
        )}

        {/* ═══ Dialogs ═══════════════════════════════════════════════════════ */}

        {/* Create circle */}
        <Dialog open={showCreateCircle} onClose={() => setShowCreateCircle(false)}>
          <DialogTitle>Create New Circle</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              autoFocus label="Circle Name" fullWidth
              value={newCircleName}
              onChange={(e) => setNewCircleName(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleCreateCircle(); }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">Colour</Typography>
              <input type="color" value={newCircleColor}
                onChange={(e) => setNewCircleColor(e.target.value)}
                style={{ width: 40, height: 32, cursor: 'pointer', border: 'none' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateCircle(false)}>Cancel</Button>
            <Button onClick={handleCreateCircle} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Edit circle */}
        <Dialog open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setEditCircle(null); setEditedName(''); }}>
          <DialogTitle>Edit Circle</DialogTitle>
          <DialogContent>
            <TextField autoFocus margin="dense" label="Circle Name" fullWidth
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleEditSubmit(); }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setEditDialogOpen(false); setEditCircle(null); setEditedName(''); }}>Cancel</Button>
            <Button onClick={handleEditSubmit} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Add member - single dialog: search → select → confirm */}
        <Dialog open={addMemberOpen} onClose={closeAddMember} maxWidth="sm" fullWidth>
          <DialogTitle>
            {addMemberPerson ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={() => setAddMemberPerson(null)}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                Add {addMemberPerson.customer_name} to {addMemberCircle?.name}
              </Box>
            ) : (
              `Add Member to ${addMemberCircle?.name}`
            )}
          </DialogTitle>

          <DialogContent>
            {!addMemberPerson ? (
              /* Step 1 - search */
              <>
                <Box component="form" onSubmit={handleAddMemberSearch} sx={{ mb: 2, mt: 1 }}>
                  <TextField
                    fullWidth autoFocus
                    placeholder="Search by name or username..."
                    value={addMemberSearch}
                    onChange={(e) => { setAddMemberSearch(e.target.value); setAddMemberSearchedOnce(false); }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button type="submit" variant="contained" size="small" disabled={addMemberLoading}>
                            {addMemberLoading ? <CircularProgress size={16} /> : 'Search'}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <List disablePadding>
                  {addMemberResults.map(person => (
                    <ListItem
                      key={person.customer_id}
                      button
                      onClick={() => setAddMemberPerson(person)}
                      sx={{ borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <Avatar sx={{ width: 36, height: 36, mr: 2 }}>
                        {(person.customer_name || '?')[0].toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={person.customer_name}
                        secondary={person.username ? `@${person.username}` : undefined}
                      />
                    </ListItem>
                  ))}
                  {addMemberResults.length === 0 && !addMemberLoading && (
                    <ListItem>
                      <ListItemText secondary={
                        addMemberSearchedOnce
                          ? "No connections match. Only your accepted connections can be added to a circle."
                          : "Only your accepted connections can be added to a circle."
                      } />
                    </ListItem>
                  )}
                </List>
              </>
            ) : (
              /* Step 2 - history sharing */
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Share event history with {addMemberPerson?.customer_name}?
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  Choose which past events shared in this circle they can see.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant={!addMemberShareHistory ? 'contained' : 'outlined'}
                    onClick={() => { setAddMemberShareHistory(false); setAddMemberHistoryDays(''); }}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    New events only
                  </Button>
                  <Button
                    size="small"
                    variant={addMemberShareHistory ? 'contained' : 'outlined'}
                    onClick={() => setAddMemberShareHistory(true)}
                    sx={{ textTransform: 'none', borderRadius: 2 }}
                  >
                    Share history
                  </Button>
                </Box>
                {addMemberShareHistory && (
                  <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="All history"
                      label="Past days"
                      value={addMemberHistoryDays}
                      onChange={(e) => setAddMemberHistoryDays(e.target.value.replace(/[^0-9]/g, ''))}
                      InputProps={{ inputProps: { min: 1 } }}
                      sx={{ width: 140 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {addMemberHistoryDays
                        ? `Events from the past ${addMemberHistoryDays} day${addMemberHistoryDays === '1' ? '' : 's'}`
                        : 'Leave blank to share all past history'}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={closeAddMember}>Cancel</Button>
            {addMemberPerson && (
              <Button
                onClick={handleConfirmAddMember}
                variant="contained"
                disabled={addMemberBusy}
              >
                {addMemberBusy ? <CircularProgress size={18} /> : 'Add Member'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
        {/* Manage circle members */}
        <Dialog
          open={manageMembersOpen}
          onClose={() => setManageMembersOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon fontSize="small" />
                <Typography variant="h6">{manageMembersCircle?.name} - Members</Typography>
              </Box>
              <IconButton size="small" onClick={() => setManageMembersOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0 }}>
            {manageMembersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : manageMembersData.length === 0 ? (
              <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No members yet. Add someone to get started.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {manageMembersData.map((member, idx) => (
                  <React.Fragment key={member.customer_id}>
                    {idx > 0 && <Divider />}
                    <ListItem sx={{ py: 1.5, px: 2 }}>
                      <Avatar sx={{ width: 38, height: 38, mr: 2, bgcolor: theme.palette.primary.light }}>
                        {(member.customer_name || '?')[0].toUpperCase()}
                      </Avatar>
                      <ListItemText
                        sx={{ minWidth: 0 }}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {member.customer_name}
                            </Typography>
                            {member.username && (
                              <Typography variant="body2" color="text.secondary">@{member.username}</Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                            {member.share_history ? (
                              <Chip
                                label={member.history_days ? `History: ${member.history_days}d` : 'All history'}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            ) : (
                              <Chip
                                label="New events only"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </Box>
                        }
                      />
                      <Tooltip title="Remove from circle">
                        <span>
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMemberFromManage(member)}
                            disabled={removingMemberId === member.customer_id}
                          >
                            {removingMemberId === member.customer_id
                              ? <CircularProgress size={18} />
                              : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
            <Button
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setManageMembersOpen(false);
                openAddMember(manageMembersCircle);
              }}
              sx={{ textTransform: 'none' }}
            >
              Add Member
            </Button>
            <Button onClick={() => setManageMembersOpen(false)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <AddToCirclePrompt
          open={!!addToCirclePrompt}
          onClose={() => setAddToCirclePrompt(null)}
          requestId={addToCirclePrompt?.requestId}
          personName={addToCirclePrompt?.personName}
        />

      </Container>
    </Box>
  );
};

export default Network;
